import React from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { COMPANY } from "../lib/constants";

const BRAND_LOGOS = [
  { src: "/brands/michelin.png", alt: "Michelin" },
  { src: "/brands/continental.png", alt: "Continental" },
  { src: "/brands/bridgestone.png", alt: "Bridgestone" },
  { src: "/brands/pirelli.png", alt: "Pirelli" },
  { src: "/brands/goodyear.png", alt: "Goodyear" },
  { src: "/brands/dunlop.png", alt: "Dunlop" },
  { src: "/brands/uniroyal.png", alt: "Uniroyal" },
  { src: "/brands/laufenn.png", alt: "Laufenn" },
  { src: "/brands/sailun.png", alt: "Sailun" },
  { src: "/brands/taurus.png", alt: "Taurus" },
];

const ColumnTitle = ({ children }) => (
  <h4 className="font-display uppercase text-white/50 text-[11px] tracking-[0.3em] mb-6">
    {children}
  </h4>
);

export const Footer = () => {
  return (
    <footer
      data-testid="footer-section"
      className="bg-[#081A33] border-t border-[#2F568F]"
    >
      {/* Brand carousel — monochrome, infinite slow scroll, color on hover */}
      <div
        data-testid="footer-brand-carousel"
        className="border-b border-[#2F568F]/60 py-8 overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)",
        }}
      >
        <div className="flex w-max animate-brand-marquee gap-10 md:gap-14">
          {[...BRAND_LOGOS, ...BRAND_LOGOS].map((b, i) => (
            <div
              key={`${b.alt}-${i}`}
              className="flex items-center justify-center w-28 md:w-32 h-10 md:h-12 flex-shrink-0"
            >
              <img
                src={b.src}
                alt={b.alt}
                className="max-h-full max-w-full object-contain opacity-90 hover:opacity-100 hover:scale-110 transition-all duration-500"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="w-full px-6 md:px-12 lg:px-20 py-14 md:py-16">
        {/* Brand row */}
        <div className="flex flex-row items-center justify-between gap-4 pb-10 md:pb-12 border-b border-[#2F568F]/40">
          <div className="flex items-center gap-3 md:gap-4 min-w-0">
            <img
              src={COMPANY.logo}
              alt="Global Pneus"
              className="h-10 w-10 md:h-14 md:w-14 object-contain flex-shrink-0"
            />
            <div className="font-display font-bold uppercase leading-[0.9] tracking-tight">
              <div className="bg-gradient-to-r from-[#FF5A00] to-[#FFD4B0] bg-clip-text text-transparent text-lg sm:text-2xl md:text-3xl">
                Global
              </div>
              <div className="bg-gradient-to-r from-[#FFD4B0] to-[#FF5A00] bg-clip-text text-transparent text-lg sm:text-2xl md:text-3xl">
                Pneus
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-5 flex-shrink-0">
            <img
              src="/logo-confortauto-nav.png"
              alt="ConfortAuto"
              className="h-7 sm:h-8 md:h-10 w-auto object-contain"
            />
            <img
              src="/logo-hankook.png"
              alt="Hankook Masters"
              className="h-4 sm:h-5 md:h-6 w-auto object-contain"
            />
          </div>
        </div>

        {/* Single column — Contactos */}
        <div className="grid grid-cols-1 pt-10 md:pt-12 max-w-xl">
          <div>
            <ColumnTitle>Contactos</ColumnTitle>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#FF5A00] mt-1 flex-shrink-0" />
                <a
                  data-testid="footer-map-link"
                  href={COMPANY.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#E0E0E0] hover:text-[#FF5A00] transition-colors font-body"
                >
                  {COMPANY.address}
                  <span className="block text-[10px] uppercase tracking-widest text-[#A1A1AA] mt-1">
                    Ver no mapa →
                  </span>
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#FF5A00] flex-shrink-0" />
                <a
                  data-testid="footer-landline-link"
                  href={COMPANY.landlineHref}
                  className="text-sm text-[#E0E0E0] hover:text-[#FF5A00] transition-colors font-body"
                >
                  {COMPANY.landline}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#FF5A00] flex-shrink-0" />
                <a
                  data-testid="footer-email-link"
                  href={`mailto:${COMPANY.email}`}
                  className="text-sm text-[#E0E0E0] hover:text-[#FF5A00] transition-colors font-body break-all"
                >
                  {COMPANY.email}
                </a>
              </li>
              <li className="flex items-start gap-3 pt-2 border-t border-[#2F568F]/30">
                <Clock className="w-4 h-4 text-[#FF5A00] mt-1 flex-shrink-0" />
                <div>
                  {COMPANY.hours.map((h) => (
                    <div key={h.day} className="text-xs text-[#A1A1AA] font-body leading-snug">
                      <span className="text-white">{h.day}</span> · {h.time}
                    </div>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-14 pt-6 border-t border-[#2F568F]/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-[#A1A1AA] font-body">
            © {new Date().getFullYear()} {COMPANY.name}. Todos os direitos reservados.
          </p>
          <p className="text-[10px] text-[#5A7AAE] font-body uppercase tracking-[0.3em]">
            Feito em {COMPANY.city}, Portugal
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
